import { Button } from '@timelyapp/react';
import api from 'api';
import { AgentReportValue } from 'components/superreports/AgentReportValue';
import { Td, Tr } from 'components/ui-blocks/table';
import {
  AGENT_REPORT_LABELS,
  type AgentReportColumn,
  type AgentReportMetrics,
} from 'constants/agentReporting';
import React, { useEffect, useState } from 'react';
import styles from './AgentSessionRows.module.scss';

interface SessionPeriod {
  id: number;
  day: string;
  summary: string | null;
  project: string | null;
  resource_id: string | null;
  metrics: AgentReportMetrics;
}
export interface ReportSession {
  id: number;
  title: string;
  provider_name: string;
  conductor: string;
  metrics: AgentReportMetrics;
  periods: SessionPeriod[];
}
interface SessionPage {
  sessions: ReportSession[];
  total_count: number;
}
const loadSessionPage = (
  params: Record<string, unknown>,
  signal: AbortSignal
): Promise<SessionPage> => api.post('reports/filter.json', params, { abortSignal: signal });

interface Props {
  filters: Record<string, string>;
  columns: AgentReportColumn[];
  columnCount?: number;
  loadSessions?: typeof loadSessionPage;
}

export function AgentSessionList({ filters, columns, loadSessions = loadSessionPage }: Props) {
  const request = JSON.stringify(filters);
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [data, setData] = useState<{ request: string; value: SessionPage }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const sessions = data?.request === request ? data.value.sessions : [];
  const total = data?.request === request ? data.value.total_count : 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: retry deliberately restarts a failed request.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    loadSessions(
      { ...JSON.parse(request), scope: 'agent_activity', group_by_session: true, page, limit: 50 },
      controller.signal
    )
      .then(value => {
        if (controller.signal.aborted) return;
        setData(previous => ({
          request,
          value: {
            ...value,
            sessions:
              page > 1 && previous?.request === request
                ? [...previous.value.sessions, ...value.sessions]
                : value.sessions,
          },
        }));
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [loadSessions, page, request, retry]);

  return (
    <section className={styles.sessions} aria-label="Agent sessions">
      <h4>Agent sessions{total > 0 ? ` (${total})` : ''}</h4>
      {sessions.length > 0 && (
        <table aria-label="Individual agent sessions">
          <thead>
            <tr>
              <th scope="col">Session</th>
              {columns.map(column => (
                <th scope="col" key={column}>
                  {AGENT_REPORT_LABELS[column]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => (
              <tr key={session.id}>
                <th scope="row">
                  <details>
                    <summary>{session.title || 'Untitled session'}</summary>
                    <ul>
                      {session.periods.map(period => (
                        <li key={period.id}>
                          <strong>
                            {period.day} · {period.project || 'Unassigned'}
                          </strong>
                          <p>{period.summary || 'No summary recorded.'}</p>
                          {period.resource_id && <p>Linked work: {period.resource_id}</p>}
                          <span>
                            Agent time:{' '}
                            <AgentReportValue
                              metrics={period.metrics}
                              column="agent_time"
                              link={false}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                  <div className={styles.metadata}>
                    {session.provider_name} · {session.conductor} · {session.periods.length}{' '}
                    {session.periods.length === 1 ? 'period' : 'periods'}
                  </div>
                  <p className={styles.summary}>
                    {session.periods.find(period => period.summary)?.summary}
                  </p>
                </th>
                {columns.map(column => (
                  <td key={column}>
                    <AgentReportValue metrics={session.metrics} column={column} link={false} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {loading && <p role="status">Loading agent sessions…</p>}
      {error && (
        <p role="alert">
          Could not load agent sessions.{' '}
          <Button size="small" onClick={() => setRetry(value => value + 1)}>
            Try again
          </Button>
        </p>
      )}
      {!loading && !error && !sessions.length && <p>No matching agent sessions.</p>}
      {!loading && !error && sessions.length < total && (
        <Button size="small" onClick={() => setPage(value => value + 1)}>
          Load more sessions
        </Button>
      )}
    </section>
  );
}

export default function AgentSessionRows({ columnCount = 1, ...props }: Props) {
  return (
    <Tr>
      <Td colSpan={columnCount * 2}>
        <AgentSessionList {...props} />
      </Td>
    </Tr>
  );
}
